import { z } from 'zod';
import {
  mapAccountItem,
  mapAccounts,
  mapAllocationMonths,
  mapAssignResult,
  mapCaseSaved,
  mapCases,
  mapReflections,
  mapTaskRemoved,
  mapCaseRemoved,
  mapStaffRemoved,
  mapAllocationRemoved,
  mapSchedule,
  mapScheduleRowItem,
  mapStaff,
  mapStaffItem,
  mapTaskItem,
  mapTasks,
  mapUser,
} from '../contract/mappers';
import { API_PATHS, apiPath } from '../contract/paths';
import {
  AccountItemResponseSchema,
  AccountPasswordResponseSchema,
  AccountRemovedResponseSchema,
  AccountsResponseSchema,
  AllocationReplaceResponseSchema,
  AssignResponseSchema,
  CaseItemResponseSchema,
  CasesResponseSchema,
  LoginResponseSchema,
  MeResponseSchema,
  MilestonesResponseSchema,
  ReflectionsResponseSchema,
  TaskRemovedResponseSchema,
  CaseRemovedResponseSchema,
  StaffRemovedResponseSchema,
  AllocationRemovedResponseSchema,
  ScheduleResponseSchema,
  ScheduleRowResponseSchema,
  StaffItemResponseSchema,
  StaffResponseSchema,
  TaskItemResponseSchema,
  TasksResponseSchema,
  WorkingGroupsResponseSchema,
  type AccountCreate,
  type AccountUpdate,
  type AllocationReplace,
  type PasswordReset,
  type CaseWrite,
  type LoginRequest,
  type MilestonesWrite,
  type ScheduleRowWrite,
  type StaffWrite,
  type TaskWrite,
  type WorkingGroupsWrite,
} from '../contract/schemas';
import type {
  Account,
  AllocationMonth,
  AssignInput,
  AssignResult,
  Case,
  CaseSaved,
  Reflections,
  Removal,
  Schedule,
  ScheduleRow,
  SessionUser,
  Staff,
  TaskGroupKey,
  TaskRow,
} from '../domain/types';
import { createHttpClient } from '../http/client';

export type Api = {
  getCases: () => Promise<Case[]>;
  getStaff: () => Promise<Staff[]>;
  getTasks: () => Promise<TaskRow[]>;
  getSchedule: () => Promise<Schedule>;
  getReflections: () => Promise<Reflections>;
  assign: (input: AssignInput) => Promise<AssignResult>;
  reflect: (input: { group: TaskGroupKey; ninku: number }) => Promise<void>;

  login: (input: LoginRequest) => Promise<{ token: string; user: SessionUser }>;
  getMe: () => Promise<SessionUser>;
  logout: () => Promise<void>;

  createTask: (body: TaskWrite) => Promise<TaskRow>;
  updateTask: (id: string, body: TaskWrite) => Promise<TaskRow>;
  deleteTask: (id: string) => Promise<Removal>;
  saveScheduleRow: (taskId: string, body: ScheduleRowWrite) => Promise<ScheduleRow>;
  saveMilestones: (body: MilestonesWrite) => Promise<void>;
  saveWorkingGroups: (body: WorkingGroupsWrite) => Promise<void>;
  createCase: (body: CaseWrite) => Promise<CaseSaved>;
  updateCase: (id: string, body: CaseWrite) => Promise<CaseSaved>;
  deleteCase: (id: string) => Promise<Removal>;
  createStaff: (body: StaffWrite) => Promise<Staff>;
  updateStaff: (id: string, body: StaffWrite) => Promise<Staff>;
  deleteStaff: (id: string) => Promise<Removal>;
  replaceAllocations: (
    staffId: string,
    caseId: string,
    body: AllocationReplace,
  ) => Promise<AllocationMonth[]>;
  removeAllocations: (staffId: string, caseId: string) => Promise<Removal>;

  getAccounts: () => Promise<Account[]>;
  createAccount: (body: AccountCreate) => Promise<Account>;
  updateAccount: (id: string, body: AccountUpdate) => Promise<Account>;
  resetAccountPassword: (id: string, body: PasswordReset) => Promise<void>;
  deleteAccount: (id: string) => Promise<void>;
};

type ApiOptions = {
  baseUrl: string;
  fetchImpl?: typeof fetch;
  getToken?: () => string | null;
  onUnauthorized?: (token: string) => void;
};

export const createApi = ({ baseUrl, fetchImpl, getToken, onUnauthorized }: ApiOptions): Api => {
  const http = createHttpClient({
    baseUrl,
    ...(fetchImpl === undefined ? {} : { fetchImpl }),
    ...(getToken === undefined ? {} : { getToken }),
    ...(onUnauthorized === undefined ? {} : { onUnauthorized }),
  });
  return {
    getCases: async () => mapCases(await http.get(API_PATHS.cases, CasesResponseSchema)),
    getStaff: async () => mapStaff(await http.get(API_PATHS.staff, StaffResponseSchema)),
    getTasks: async () => mapTasks(await http.get(API_PATHS.tasks, TasksResponseSchema)),
    getSchedule: async () =>
      mapSchedule(await http.get(API_PATHS.schedule, ScheduleResponseSchema)),
    getReflections: async () =>
      mapReflections(await http.get(API_PATHS.reflections, ReflectionsResponseSchema)),
    assign: async (input) =>
      mapAssignResult(await http.send('POST', API_PATHS.allocations, input, AssignResponseSchema)),
    reflect: async (input) => {
      await http.send('PUT', API_PATHS.reflect, input, z.unknown());
    },

    login: async (input) => {
      const res = await http.send('POST', API_PATHS.login, input, LoginResponseSchema);
      return { token: res.token, user: mapUser(res.user) };
    },
    getMe: async () => mapUser((await http.get(API_PATHS.me, MeResponseSchema)).user),
    logout: async () => {
      await http.send('POST', API_PATHS.logout, {}, z.unknown());
    },

    createTask: async (body) =>
      mapTaskItem((await http.send('POST', API_PATHS.tasks, body, TaskItemResponseSchema)).item),
    updateTask: async (id, body) =>
      mapTaskItem(
        (await http.send('PUT', apiPath.taskById(id), body, TaskItemResponseSchema)).item,
      ),
    deleteTask: async (id) =>
      mapTaskRemoved(await http.delete(apiPath.taskById(id), TaskRemovedResponseSchema)),
    saveScheduleRow: async (taskId, body) =>
      mapScheduleRowItem(
        (await http.send('PUT', apiPath.scheduleRow(taskId), body, ScheduleRowResponseSchema)).item,
      ),
    saveMilestones: async (body) => {
      await http.send('PUT', API_PATHS.scheduleMilestones, body, MilestonesResponseSchema);
    },
    saveWorkingGroups: async (body) => {
      await http.send('PUT', API_PATHS.scheduleWorkingGroups, body, WorkingGroupsResponseSchema);
    },

    createCase: async (body) =>
      mapCaseSaved(await http.send('POST', API_PATHS.cases, body, CaseItemResponseSchema)),
    updateCase: async (id, body) =>
      mapCaseSaved(await http.send('PUT', apiPath.caseById(id), body, CaseItemResponseSchema)),
    deleteCase: async (id) =>
      mapCaseRemoved(await http.delete(apiPath.caseById(id), CaseRemovedResponseSchema)),

    createStaff: async (body) =>
      mapStaffItem((await http.send('POST', API_PATHS.staff, body, StaffItemResponseSchema)).item),
    updateStaff: async (id, body) =>
      mapStaffItem(
        (await http.send('PUT', apiPath.staffById(id), body, StaffItemResponseSchema)).item,
      ),
    deleteStaff: async (id) =>
      mapStaffRemoved(await http.delete(apiPath.staffById(id), StaffRemovedResponseSchema)),

    replaceAllocations: async (staffId, caseId, body) =>
      mapAllocationMonths(
        await http.send(
          'PUT',
          apiPath.allocationPair(staffId, caseId),
          body,
          AllocationReplaceResponseSchema,
        ),
      ),
    removeAllocations: async (staffId, caseId) =>
      mapAllocationRemoved(
        await http.delete(apiPath.allocationPair(staffId, caseId), AllocationRemovedResponseSchema),
      ),

    getAccounts: async () =>
      mapAccounts(await http.get(API_PATHS.accounts, AccountsResponseSchema)),
    createAccount: async (body) =>
      mapAccountItem(
        (await http.send('POST', API_PATHS.accounts, body, AccountItemResponseSchema)).item,
      ),
    updateAccount: async (id, body) =>
      mapAccountItem(
        (await http.send('PUT', apiPath.accountById(id), body, AccountItemResponseSchema)).item,
      ),
    resetAccountPassword: async (id, body) => {
      await http.send('PUT', apiPath.accountPassword(id), body, AccountPasswordResponseSchema);
    },
    deleteAccount: async (id) => {
      await http.delete(apiPath.accountById(id), AccountRemovedResponseSchema);
    },
  };
};
